import { Image } from 'expo-image';

import { queryClient } from '@fhs/client';
import { imageUrlBuilder, nativeHomepageHeroQuery } from '@fhs/cms-groq-queries';
import { performSanityQuery } from '@fhs/cms-groq-queries/src/client';

import { preloadHeroVideo } from './hero-video-preload';

type PosterSource = Parameters<typeof imageUrlBuilder.image>[0];

export type NativeHomepageHero = {
  isLtoActive?: boolean | null;
  ltoName?: string | null;
  videoUrl?: string | null;
  poster?: PosterSource | null;
} | null;

// How soon a CMS edit (LTO toggle / swapped video) is picked up.
const HERO_STALE_TIME_MS = __DEV__ ? 0 : 1000 * 60 * 5;

export const nativeHomepageHeroQueryOptions = {
  queryKey: ['native-homepage-hero-v0'] as const,
  queryFn: () =>
    performSanityQuery<NativeHomepageHero>({ groqQueryString: nativeHomepageHeroQuery }),
  // Serve the persisted cache instantly (paints on warm/cold starts), then
  // revalidate in the background; new data crossfades in via the widget.
  staleTime: HERO_STALE_TIME_MS,
  refetchOnMount: true,
  refetchOnReconnect: true,
  meta: { persist: true },
};

// Builds poster URL
export function getNativeHomepageHeroPosterUrl(
  poster: PosterSource | null | undefined
): string | undefined {
  if (!poster) {
    return undefined;
  }
  return imageUrlBuilder.image(poster).format('webp').url();
}

// Fire the Sanity request at app startup so it's in flight before the home screen
// mounts. On a warm start the persisted data is still fresh, so this is a no-op.
export async function prefetchNativeHomepageHero(): Promise<void> {
  await queryClient.prefetchQuery(nativeHomepageHeroQueryOptions);

  const hero = queryClient.getQueryData<NativeHomepageHero>(
    nativeHomepageHeroQueryOptions.queryKey
  );
  if (hero?.poster) {
    const posterUrl = getNativeHomepageHeroPosterUrl(hero.poster);
    if (posterUrl) {
      Image.prefetch(posterUrl).catch(() => {});
    }
  }

  // Warm the hero video before the home screen mounts so its first frame reads
  // from a filled buffer (iOS, in memory) / disk cache (Android) instead of a
  // cold network fetch. The widget adopts this exact player instance. The CMS
  // call above already runs at bootstrap, so the URL is available this early.
  if (hero?.videoUrl) {
    preloadHeroVideo(hero.videoUrl);
  }
}
